import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useHandleDirtyFieldFlag } from 'src/@core/hooks/useHandleDirtyFieldsFlag';
import { useRegisterActiveTabFunctions } from 'src/@core/hooks/useRegisterActiveTabFunctions';
import { AdditionalDataPanel, useAdditionalDataPanel } from './useAdditionalDataPanel';
import { useRegisterCustomHeaderComponent } from './useRegisterCustomHeaderComponent';
import { useRecentHistory } from './useRecentHistory';
import { useAdditionalDataTransferObject } from './useAdditionalDataTransferObject';
import { useAppDispatch, useAppSelector } from 'src/store/baseHooks';
import { resetToFixedPanel, setFormContextResolver } from 'src/store/slices/uiFlowSlice';

/**
 * Hook which handles common hooks used in a typical UIComponent.
 *
 * - Setting the dirty flag.
 * - Registering custom header components.
 * - Registering active tab functions.
 * - Registering tab level additional data.
 * - Setting the validation schema for forms.
 */

interface useUIFlowTabComponentProps {
  defaultFormData?: Record<string, any>;
  schemaResolver?: Record<string, any>;
  activeTabFunctions?: {
    [key: string]: (...rest: any[]) => any;
  } /* Functions to used by the toolbar buttons. E.g save */;
  customHeaderComponentName?: string /* Name of component to render in the item view header component - This replaces the text set using the headerTitlePropertyName property */;
  onSuccess?: (x?: any) => any /* Optional - success function to call when data fetch is complete */;
  dataProperty?: string /* Optional - specify a property of the data to use for the form */;
  additionalDataOptions?: AdditionalDataPanel /* Properties to set for the additional data panel - at tab level */;
}

const useUIFlowTabComponent = ({
  defaultFormData = {},
  schemaResolver = undefined,
  activeTabFunctions = {},
  customHeaderComponentName = '',
  additionalDataOptions = undefined
}: useUIFlowTabComponentProps) => {
  const methods = useFormContext();

  const {
    control,
    formState: { dirtyFields },
    getValues,
    reset,
    trigger,
    setValue: setFormValue,
    clearErrors
  } = methods;

  const isDirty = !!Object.keys(dirtyFields ?? {}).length;

  const { addRecentHistory } = useRecentHistory();
  const { set, get, deRegister } = useAdditionalDataTransferObject();
  useHandleDirtyFieldFlag(isDirty);
  useRegisterCustomHeaderComponent(customHeaderComponentName);
  const { addAdditionalDataPanel } = useAdditionalDataPanel();

  const dispatch = useAppDispatch();
  const uiFlowMode = useAppSelector(state => state.uiflow.uiFlowMode);

  useRegisterActiveTabFunctions(activeTabFunctions);

  useEffect(() => {
    /* Reset form state to default when uiFlowMode = add */
    if (uiFlowMode === 'add') {
      reset(defaultFormData);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiFlowMode]);

  /* Reset additional data properties on unmount */
  useEffect(() => {
    if (additionalDataOptions) {
      addAdditionalDataPanel(additionalDataOptions!);
    }

    return () => {
      dispatch(resetToFixedPanel());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!schemaResolver) return;
    dispatch(setFormContextResolver(schemaResolver));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemaResolver]);

  return {
    methods,
    control,
    getValues,
    reset,
    addRecentHistory,
    set,
    get,
    deRegister,
    setFormValue,
    isDirty,
    trigger,
    clearErrors,
    dirtyFields
  };
};

export default useUIFlowTabComponent;
